import Moment from 'moment/moment'
import momentTz from 'moment-timezone'
import compact from 'lodash/compact'
import isEmpty from 'lodash/isEmpty'
import type { DateType } from './DateType'
import { hasWindow } from '../has-window'

export type { DateType } from './DateType'

/**
 * Converts a given date to a Moment object.
 * If no date is provided, current date is used.
 * @param date - The date to be converted
 * @param timeZone - The time zone to be used for conversion
 * @param options - Additional options for the conversion
 * @param options.keepLocalTime - If true, keeps the local time
 * @returns The Moment object representing the converted date
 */
export const toDate = (
  date?: DateType,
  timeZone?: string,
  options?: {
    keepLocalTime?: boolean
  }
): Moment.Moment => {
  //if the date is type of null or empty string, it should always be current date
  let momentObj = Moment(date)

  //if the date is number is should be unix timestamp
  if (typeof date === 'number') {
    momentObj = Moment(date * 1000)
  }

  //when timezone is included, use the timezone offset
  if (timeZone) {
    return momentTz(momentObj).tz(timeZone, options?.keepLocalTime)
  }
  return momentObj
}

/**
 * Returns the browser's IANA timezone (e.g., 'Asia/Manila', 'America/New_York').
 * Falls back to undefined in non-browser contexts or if the Intl API is unavailable.
 *
 * Use this in client-side utilities that need a timezone but cannot depend on
 * the authenticated user (e.g., form inputs that may render before login or in
 * public pages). It reads the timezone from the user's browser, which typically
 * matches the user's actual local timezone regardless of auth state.
 */
export const getBrowserTimeZone = (): string | undefined => {
  if (!hasWindow()) return undefined
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
  } catch {
    return undefined
  }
}

export const timeFromNow = (
  date: DateType,
  timeZone?: string,
  options?: {
    keepLocalTime?: boolean
  }
) => {
  return toDate(date, timeZone, options).fromNow()
}

/**
 * Returns the earliest of the given Moment instances. Thin wrapper around
 * Moment.min so consumers do not need to import moment directly.
 */
export const dateMin = (dates: Moment.Moment[]): Moment.Moment => Moment.min(dates)

/**
 * Returns the latest of the given Moment instances. Thin wrapper around
 * Moment.max so consumers do not need to import moment directly.
 */
export const dateMax = (dates: Moment.Moment[]): Moment.Moment => Moment.max(dates)

// -----------------------------------------------------------------------------
// Date filter presets
// Previously lived in ../filter-helper. Moved here so all date-related
// conversions and date-filter presets are colocated.
// -----------------------------------------------------------------------------

export enum DateTagNameEnum {
  STARTOFYEAR = 'STARTOFYEAR',
  ENDOFYEAR = 'ENDOFYEAR',
  STARTOFMONTH = 'STARTOFMONTH',
  ENDOFMONTH = 'ENDOFMONTH',
  STARTOFWEEK = 'STARTOFWEEK',
  ENDOFWEEK = 'ENDOFWEEK',
  STARTOFDAY = 'STARTOFDAY',
  ENDOFDAY = 'ENDOFDAY',
  CURRENT = 'CURRENT',
}

export enum DateIsEnum {
  DateIs = 'Date is',
  DateIsBefore = 'Date is before',
  DateIsAfter = 'Date is after',
  DateIsBetween = 'Date is between',
  DateIsBlank = '(blank)',
}

export enum DateNameEnum {
  Today = 'Today',
  Tomorrow = 'Tomorrow',
  Yesterday = 'Yesterday',
  ThisWeek = 'This week',
  LastWeek = 'Last Week',
  NextWeek = 'Next Week',
  ThisMonth = 'This Month',
  LastMonth = 'Last Month',
  NextMonth = 'Next Month',
  ThisYear = 'This Year',
  LastYear = 'Last Year',
  NextYear = 'Next Year',
  ExactDate = 'Exact Date',
}

export type DateRange = { $gte?: string; $lte?: string; $eq?: string | null }

export const DateNameValue: Record<DateNameEnum, DateRange> = {
  [DateNameEnum.Today]: {
    $gte: `<<${DateTagNameEnum.STARTOFDAY}>>`,
    $lte: `<<${DateTagNameEnum.ENDOFDAY}>>`,
  },
  [DateNameEnum.Tomorrow]: {
    $gte: `<<${DateTagNameEnum.STARTOFDAY} 1 days>>`,
    $lte: `<<${DateTagNameEnum.ENDOFDAY} 1 days>>`,
  },
  [DateNameEnum.Yesterday]: {
    $gte: `<<${DateTagNameEnum.STARTOFDAY} -1 days>>`,
    $lte: `<<${DateTagNameEnum.ENDOFDAY} -1 days>>`,
  },
  [DateNameEnum.ThisWeek]: {
    $gte: `<<${DateTagNameEnum.STARTOFWEEK}>>`,
    $lte: `<<${DateTagNameEnum.ENDOFWEEK}>>`,
  },
  [DateNameEnum.LastWeek]: {
    $gte: `<<${DateTagNameEnum.STARTOFWEEK} -1 weeks>>`,
    $lte: `<<${DateTagNameEnum.ENDOFWEEK} -1 weeks>>`,
  },
  [DateNameEnum.NextWeek]: {
    $gte: `<<${DateTagNameEnum.STARTOFWEEK} 1 weeks>>`,
    $lte: `<<${DateTagNameEnum.ENDOFWEEK} 1 weeks>>`,
  },
  [DateNameEnum.ThisMonth]: {
    $gte: `<<${DateTagNameEnum.STARTOFMONTH}>>`,
    $lte: `<<${DateTagNameEnum.ENDOFMONTH}>>`,
  },
  [DateNameEnum.LastMonth]: {
    $gte: `<<${DateTagNameEnum.STARTOFMONTH} -1 months>>`,
    $lte: `<<${DateTagNameEnum.ENDOFMONTH} -1 months>>`,
  },
  [DateNameEnum.NextMonth]: {
    $gte: `<<${DateTagNameEnum.STARTOFMONTH} 1 months>>`,
    $lte: `<<${DateTagNameEnum.ENDOFMONTH} 1 months>>`,
  },
  [DateNameEnum.ThisYear]: {
    $gte: `<<${DateTagNameEnum.STARTOFYEAR}>>`,
    $lte: `<<${DateTagNameEnum.ENDOFYEAR}>>`,
  },
  [DateNameEnum.LastYear]: {
    $gte: `<<${DateTagNameEnum.STARTOFYEAR} -1 years>>`,
    $lte: `<<${DateTagNameEnum.ENDOFYEAR} -1 years>>`,
  },
  [DateNameEnum.NextYear]: {
    $gte: `<<${DateTagNameEnum.STARTOFYEAR} 1 years>>`,
    $lte: `<<${DateTagNameEnum.ENDOFYEAR} 1 years>>`,
  },
  [DateNameEnum.ExactDate]: {
    $eq: null,
  },
}

/**
 * Resolves a filter string containing date tags (e.g. `<<STARTOFDAY>>`,
 * `<<ENDOFWEEK 1 weeks>>`) into concrete ISO date strings for the given
 * timezone. Used by the data-tools filter bar to evaluate preset date
 * ranges at query time.
 */
export function getDynamicDateFilter({
  filterString,
  timeZone = 'America/Chicago',
}: {
  filterString: string
  timeZone?: string
}) {
  const startOrEnd = (value: string) => {
    const valueArr = value?.split('OF')
    const result: { function?: 'startOf' | 'endOf'; unit?: string } = {}
    if (valueArr?.length === 2) {
      result.function = valueArr[0] === 'START' ? 'startOf' : 'endOf'
      result.unit = valueArr[1].toLowerCase()
    }

    return result
  }
  if (isEmpty(filterString)) return ''

  return filterString.replace(/<<(.*?)>>/g, (match) => {
    //split the value
    const fieldName = match.split(/<<|>>/).filter(Boolean)[0]

    //split the value
    let valueArr = fieldName.split(' ') ?? []
    valueArr = compact(valueArr)

    let currentDate = toDate(undefined, timeZone, { keepLocalTime: true }) // momentTz().tz(timeZone)
    const startOrEndOf = startOrEnd(valueArr?.[0])
    if (!isEmpty(startOrEndOf)) {
      currentDate = currentDate?.[startOrEndOf.function as 'startOf' | 'endOf'](
        startOrEndOf.unit as 'year' | 'month' | 'week' | 'day'
      )
    }

    //there are 3 items, value , days, unit
    if (valueArr?.length === 3) {
      currentDate.add(valueArr[1] as unknown as number, valueArr[2] as 'days' | 'weeks' | 'months' | 'years')
    }

    return currentDate?.toISOString()
  })
}

/**
 * Finds the DateNameEnum key that matches the provided date range values.
 * This function is used to identify which preset filter (Today, This Week, etc.)
 * corresponds to the stored filter values ($gte and $lte).
 *
 * @param gteValue - The greater than or equal date string (start of range)
 * @param lteValue - The less than or equal date string (end of range)
 * @returns The matching DateNameEnum key, or null if no preset matches
 *
 * @example
 * ```ts
 * const preset = findKeyByDateRange('<<STARTOFDAY>>', '<<ENDOFDAY>>')
 * // Returns: DateNameEnum.Today
 *
 * const custom = findKeyByDateRange('2024-01-01', '2024-01-31')
 * // Returns: null (custom range, not a preset)
 * ```
 */
export function findKeyByDateRange(gteValue: string, lteValue: string): DateNameEnum | null {
  // Guard against invalid inputs
  if (!gteValue || !lteValue) return null

  for (const [key, range] of Object.entries(DateNameValue)) {
    if (range.$gte === gteValue && range.$lte === lteValue) {
      return key as DateNameEnum
    }
  }
  return null
}

/**
 * Determines if a DateRange represents a custom range (absolute dates)
 * rather than a preset (dynamic tags like <<STARTOFDAY>>)
 * Accepts both string ISO dates and Date objects as custom ranges
 */
export function isCustomDateRange(range: DateRange): boolean {
  if (!range?.$gte || !range?.$lte) return false

  // Cast to any for type checking since DateRange type only defines string
  // but at runtime values can be Date objects
  const gte = range.$gte as any
  const lte = range.$lte as any

  // Reject non-string, non-Date types (numbers, objects, etc.)
  const gteType = typeof gte
  const lteType = typeof lte
  const gteIsDate = gte instanceof Date
  const lteIsDate = lte instanceof Date

  // Only accept strings or Date objects
  if (!((gteType === 'string' || gteIsDate) && (lteType === 'string' || lteIsDate))) {
    return false
  }

  // If values are strings, check if they contain dynamic tags (presets)
  if (gteType === 'string' && lteType === 'string') {
    // Validate that strings are valid dates
    const gteDate = toDate(gte)
    const lteDate = toDate(lte)
    if (!gteDate.isValid() || !lteDate.isValid()) return false

    // Check for dynamic tags (presets)
    const hasDynamicTag = (str: string) => str.includes('<<') && str.includes('>>')
    return !hasDynamicTag(gte) && !hasDynamicTag(lte)
  }

  // If values are Date objects, they represent custom ranges
  return true
}
